/**
 * 3D Visualizer — Extruded Board Builder
 *
 * Builds a real 3D board by extruding a 2D cross-section profile along
 * a board's run length. Replaces flat-plane SlatWall rendering so the
 * grooves and locking edges become actual geometry that catches light
 * and casts soft self-shadows.
 *
 * Output orientation (canonical — caller transforms per wall face):
 *   - mesh.X ∈ [0, length]        run direction (along wall)
 *   - mesh.Y ∈ [0, profile.verticalSize]   bottom-to-top of panel
 *   - mesh.Z ∈ [0, profile.depthSize]      wall plane to room interior
 */

import * as THREE from 'three';
import { CrossSectionProfile } from './profiles/profileRegistry';
import { WallFace } from './types';

/**
 * Build a single extruded board mesh in canonical front-face wall coords.
 * Position it via mesh.position to place the bottom-left corner of the
 * board's run; apply per-face transforms via the parent wall group.
 */
export function buildExtrudedBoardGeometry(
  profile: CrossSectionProfile,
  length: number,
): THREE.ExtrudeGeometry {
  // 1. Build a closed THREE.Shape from the profile points. The shape's
  //    local X axis = profile depth, local Y axis = profile vertical.
  const shape = new THREE.Shape();
  const pts = profile.points;
  shape.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) {
    shape.lineTo(pts[i][0], pts[i][1]);
  }
  shape.closePath();

  // 2. Extrude along the shape's local Z axis by `length`. After this:
  //    vertex.X = depth   ∈ [0, profile.depthSize]
  //    vertex.Y = vertical ∈ [0, profile.verticalSize]
  //    vertex.Z = run      ∈ [0, length]
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: length,
    bevelEnabled: false,
    steps: 1,
  });

  // 3. Reorient so the run axis becomes mesh.X (matching wall-local
  //    convention used by the rest of wallMeshBuilder).
  //    rotateY(-π/2) maps (x, y, z) → (-z, y, x).
  geometry.rotateY(-Math.PI / 2);
  // Run axis is now negative X (-length to 0); translate +length to flip
  // it positive. Final: mesh.X ∈ [0, length], mesh.Y unchanged,
  // mesh.Z = old depth ∈ [0, profile.depthSize].
  geometry.translate(length, 0, 0);

  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Build a single extruded board mesh in canonical front-face wall coords.
 * Position it via mesh.position to place the bottom-left corner of the
 * board's run; apply per-face transforms via the parent wall group.
 */
export function buildExtrudedBoard(
  profile: CrossSectionProfile,
  length: number,
  material: THREE.Material,
): THREE.Mesh {
  const geometry = buildExtrudedBoardGeometry(profile, length);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Apply the per-face transform to a Group containing canonical-orientation
 * board meshes. Mirrors the convention used by wallMeshBuilder's
 * boardTo3D helper so extruded boards end up in the same world position
 * as flat boards would.
 *
 * For 'left' and 'right' faces, the matrix is a reflection (det = -1)
 * because c.x → world.z direct mapping requires swapping X and Z. To
 * keep triangle winding correct, we also reverse the index buffer of
 * any meshes inside.
 */
export function applyFaceTransformToGroup(
  group: THREE.Group,
  face: WallFace,
  roomLength: number,
  roomWidth: number,
): void {
  switch (face) {
    case 'front':
      // identity
      break;
    case 'back':
      group.position.set(roomLength, 0, roomWidth);
      group.rotation.y = Math.PI;
      break;
    case 'left':
      // (x, y, z) → (z, y, x). Reflection — apply a reflection matrix
      // directly to each child geometry and flip its winding.
      reflectChildrenSwapXZ(group);
      break;
    case 'right':
      // (x, y, z) → (roomLength - z, y, roomWidth - x). Also a reflection.
      reflectChildrenSwapXZ(group, roomLength, roomWidth);
      break;
  }
}

function reflectChildrenSwapXZ(
  group: THREE.Group,
  worldLengthOffset = 0,
  worldDepthOffset = 0,
): void {
  // (x, y, z) → (worldLengthOffset - z, y, worldDepthOffset - x) when
  // both offsets are roomLength/roomWidth (right face) — flips X/Z and
  // mirrors. With offsets 0, it's just the X↔Z swap (left face).
  const mirrorX = worldLengthOffset !== 0; // right face mirrors
  const mirrorZ = worldDepthOffset !== 0;  // right face mirrors

  group.traverse((child) => {
    if (child instanceof THREE.Mesh && child.geometry instanceof THREE.BufferGeometry) {
      const geom = child.geometry;
      const pos = geom.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const z = pos.getZ(i);
        const newX = mirrorX ? worldLengthOffset - z : z;
        const newZ = mirrorZ ? worldDepthOffset - x : x;
        pos.setX(i, newX);
        pos.setZ(i, newZ);
      }
      pos.needsUpdate = true;

      // Reflection inverts triangle winding — reverse index order so
      // front faces stay front faces (otherwise lighting renders inside-out).
      if (geom.index) {
        const idx = geom.index.array as Uint16Array | Uint32Array;
        for (let i = 0; i < idx.length; i += 3) {
          const tmp = idx[i + 1];
          idx[i + 1] = idx[i + 2];
          idx[i + 2] = tmp;
        }
        geom.index.needsUpdate = true;
      }

      geom.computeVertexNormals();
    }
  });
}
